import { useCallback, useMemo } from 'preact/hooks';
import { useComputed } from '@preact/signals';
import { hasSubscriptions, type Product } from '@/domain/Product';
import styles from './OtherWaysToShopSection.module.scss';
import useTranslations from '@/widgets/hooks/useTranslations.tsx';
import type { Options, Translations } from '../../config/index.ts';
import BundlesDrawer from '../../drawers/BundlesDrawer/BundlesDrawer.tsx';
import useQueryStringStore from '@/widgets/hooks/useQueryStringStore.tsx';
import type { PersistedState } from '../../Store.ts';
import SaveWithABundle from './parts/SaveWithABundle/SaveWithABundle.tsx';
import SubscriptionAction from './parts/SubscriptionAction/SubscriptionAction.tsx';
import SubscriptionOptionsDrawer from '../../drawers/SubscriptionOptionsDrawer/SubscriptionOptionsDrawer.tsx';
import useWidgetOptions from '@/widgets/hooks/useWidgetOptions.tsx';
import {
  flattenDurationsIntoSingleDuration,
  getSelectedSubscription
} from '@/domain/Subscription.ts';
import { trackAboutSubscriptionsModalOpened } from '../../Tracking.ts';
import { initialiseSubscriptionFlow } from '@/widgets/services/SubscriptionService.ts';

type OtherWaysToShopSectionProps = {
  product: Product;
};

export default function OtherWaysToShopSection({
  product
}: OtherWaysToShopSectionProps) {
  const { t } = useTranslations<Translations>();
  const { hideSubscriptionDurations, buyOptionsPlacement } =
    useWidgetOptions<Options>();
  const [{ openModal }, setState] = useQueryStringStore<PersistedState>();

  const isBundlesDrawerOpen = useComputed(
    () => openModal.value === 'bundle-options'
  );

  const closeDrawer = useCallback(() => {
    setState({ openModal: undefined });
  }, [setState]);

  const subscriptionsByDuration = hideSubscriptionDurations
    ? flattenDurationsIntoSingleDuration(product.subscriptionsByDuration)
    : product.subscriptionsByDuration;

  const { selectedSubscriptionId } = useMemo(
    () => initialiseSubscriptionFlow(subscriptionsByDuration),
    [product.subscriptionsByDuration]
  );

  const showSubscriptions = useMemo(
    () =>
      buyOptionsPlacement.includes('SUBSCRIPTIONS') &&
      hasSubscriptions(product) &&
      !product.isSubscriptionOnly,
    [product, buyOptionsPlacement]
  );

  const openSubscriptionOptionsModal = useCallback(() => {
    setState({ openModal: 'subscription-options' });

    const subscription = getSelectedSubscription(
      subscriptionsByDuration,
      selectedSubscriptionId.value
    );
    trackAboutSubscriptionsModalOpened(product, subscription);
  }, []);

  const hasBundles = Boolean(product.associatedBundles?.length);

  const modalEl = (
    <SubscriptionOptionsDrawer
      isOpen={openModal.value === 'subscription-options'}
      subscriptionsByDuration={subscriptionsByDuration}
      onClose={closeDrawer}
      product={product}
    />
  );

  // Todo: add checks refurbished machines
  if (!hasBundles && !showSubscriptions) {
    return modalEl;
  }

  return (
    <section class={styles.section}>
      <header>
        <h2 class={styles.heading}>
          {t('productDetailsHero.otherWaysToShopSection.otherWaysToShopLabel')}
        </h2>
      </header>

      <div class={styles.wrapper}>
        <SaveWithABundle bundles={product.associatedBundles ?? []} />

        {showSubscriptions && (
          <SubscriptionAction
            product={product}
            openSubscriptionOptionsModal={openSubscriptionOptionsModal}
          />
        )}

        {/* Todo: "Refurbished machines" action & drawer */}
      </div>

      {hasBundles && (
        <BundlesDrawer
          isOpen={isBundlesDrawerOpen}
          onClose={closeDrawer}
          bundles={product.associatedBundles ?? []}
        />
      )}

      {modalEl}
    </section>
  );
}
